/**
 * エラー表示の共通ヘルパー（docs/plan/error-display.md 4.A）
 *
 *   this.$handleError("076", e, { hideLoading: true })
 *   this.$handleError("072", e, { hideLoading: true, messages: { B: "ログインし直してください" } })
 *
 * 設計上の約束（84 箇所すべてがこれに依存する）:
 *   1. この関数は **絶対に例外を投げない**。呼び出し元は catch の中なので、投げても誰も拾えない
 *   2. hideLoading / hideProgress は **何より先に** 実行する。GA4 送信などが失敗しても操作不能にしない
 *   3. 生の例外オブジェクトをログへ渡さない（error.config.data に ID トークンが載るため）
 */
import { Logger } from "@/js/utils/Logger.js";
import { Analytics } from "@/js/Analytics.js";
import { AppError, classifyError, buildUserMessage, normalizeSiteNo, isDialogSuppressed, ERROR_CLASS_MESSAGES } from "@/js/utils/AppError.js";
import { getErrorSite } from "@/js/utils/errorSites.js";

/**
 * 失敗しても呼び出し側を巻き込まない実行。ヘルパー自身の異常は握りつぶさずコンソールには出す
 * @param {string} label 失敗時に出す識別名
 * @param {Function} fn
 */
function safe(label, fn) {
  try {
    return fn();
  } catch (e) {
    try {
      // Logger 自体が壊れている可能性があるので直接 console を使う
      console.error("[ErrorHandler] " + label + " に失敗", e && e.message);
    } catch (ignored) {
      // ここで諦める
    }
    return undefined;
  }
}

/**
 * 記録のみ（画面には出さない）。グローバルハンドラや、表示を別途行う箇所から使う。
 * @param {string|number} siteNo 番号（"076"）
 * @param {*} error
 * @param {object} [meta] origin（"Vue2" / "window.error" / "unhandledrejection" / "handled"）、info など
 * @returns {{ fullCode: string, cls: string, appError: AppError, site: object }}
 */
export function reportError(siteNo, error, meta = {}) {
  const no = safe("normalizeSiteNo", () => normalizeSiteNo(siteNo)) || "000";
  const site = safe("getErrorSite", () => getErrorSite(no)) || { feature: "UNKNOWN", label: "", file: "" };
  const appError =
    safe("AppError.from", () => AppError.from(error)) || new AppError({ source: "unknown", message: "例外の正規化に失敗" });
  const cls = safe("classifyError", () => classifyError(appError)) || "E";
  const fullCode = `${cls}-${no}`;
  const origin = meta.origin || "handled";

  safe("Logger.error", () =>
    Logger.error(`[${fullCode}] ${site.feature} ${site.label}`.trim(), appError.summary(), appError.causeForLog())
  );
  safe("Analytics.exception", () => Analytics.exception(fullCode, appError, site, { origin, info: meta.info }));

  return { fullCode, cls, appError, site };
}

/**
 * 記録して画面に表示する。
 * @param {object} store Vuex store
 * @param {string|number} siteNo 番号（"076"）
 * @param {*} error
 * @param {object} [options]
 * @param {boolean} [options.hideLoading]  showMessage の前に hideLoading を commit
 * @param {boolean} [options.hideProgress] showMessage の前に hideProgress を commit
 * @param {string}  [options.message]      分類に関係なく使う本文（行動文込み）。コード行は自動で付く
 * @param {object}  [options.messages]     分類ごとの本文 { A: "...", B: "..." }。message より弱く、Auth の文言表より強い
 * @returns {{ fullCode: string, cls: string, userMessage: string, appError: AppError, suppressed: boolean }}
 */
export function handleError(store, siteNo, error, options = {}) {
  // 約束 2: 何より先にローディングを消す
  if (store) {
    // 片方が失敗しても、もう片方は必ず実行する。
    // まとめて safe() で包むと hideLoading の失敗で hideProgress が飛び、
    // 「プログレスが閉じず操作不能」というこの仕組みが最も避けたい状態になる
    if (options.hideLoading) safe("hideLoading", () => store.commit("hideLoading"));
    if (options.hideProgress) safe("hideProgress", () => store.commit("hideProgress"));
  }

  try {
    // ユーザーが自分で中断した操作（認証ポップアップを閉じた等）は失敗として扱わず、ダイアログも記録も出さない
    const probe = AppError.from(error);
    if (isDialogSuppressed(probe)) {
      safe("Logger.debug", () => Logger.debug(`[${normalizeSiteNo(siteNo)}] suppressed`, probe.summary()));
      return { fullCode: "", cls: "", userMessage: "", appError: probe, site: null, suppressed: true };
    }

    const { fullCode, cls, appError, site } = reportError(siteNo, error, { origin: "handled" });
    const perClass = options.messages && options.messages[cls];
    const userMessage = buildUserMessage(cls, fullCode, options.message || perClass, appError);

    safe("showMessage", () => store && store.commit("showMessage", userMessage));

    return { fullCode, cls, userMessage, appError, site, suppressed: false };
  } catch (internal) {
    // 約束 1: ここまで来ても投げない。番号だけは出して問い合わせに繋げる
    safe("internal", () => console.error("[ErrorHandler] 内部エラー", internal && internal.message));
    const no = safe("normalizeSiteNo", () => normalizeSiteNo(siteNo)) || "000";
    const fullCode = `E-${no}`;
    const fallback = `${ERROR_CLASS_MESSAGES.E.line1}\n${ERROR_CLASS_MESSAGES.E.line2}\nエラーコード: ${fullCode}`;
    safe("showMessage(fallback)", () => store && store.commit("showMessage", fallback));
    return { fullCode, cls: "E", userMessage: fallback, appError: null, site: null, suppressed: false };
  }
}
